import { PublicAxiosInstance } from '@/api/axios'
import ConfirmModal from '@/components/modal/confirm-modal'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ColourBadge } from '@/components/ui/colour-badge'
import { TextArea } from '@/components/ui/input'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { ExemptionsAndExemptionsRegisterStatus } from '@/enum/global.enum'
import { useDateFormat } from '@/hooks/use-date-format'
import { setPageTitle } from '@/hooks/use-page-title'
import type { IExemptionReviewAndApprovalRequest } from '@/interface/exemptions-register.interface'
import { getExemptionStatusLabel } from '@/utils/exemption-status-label.utils'
import { getGlobalStatusHexColor } from '@/utils/global-status-color.utils'
import type { AxiosError } from 'axios'
import { AlertTriangle, Calendar, Check, CheckCircle, Circle, FileText, Home, Shield, Users, X } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router'

type ConfirmAction = 'submit' | 'reject'

const getErrorMessage = (err: unknown, fallback: string) =>
  (err as AxiosError<{ message: string }>)?.response?.data?.message || fallback

const DetailRow = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className='flex flex-wrap gap-2 justify-between items-center'>
    <span className='text-sm text-muted-foreground'>{label}</span>
    <span className='text-sm font-medium'>{value || 'N/A'}</span>
  </div>
)

const DetailText = ({ label, value }: { label: string; value?: string | null }) => (
  <div className='space-y-1'>
    <span className='text-sm text-muted-foreground'>{label}</span>
    <div className='text-sm text-justify whitespace-pre-wrap'>{value || 'N/A'}</div>
  </div>
)

const Section = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
  <Card className='shadow-sm rounded-sm border-0 dark:border-1'>
    <CardHeader>
      <CardTitle className='text-sm font-medium flex items-center gap-2'>
        {icon}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className='space-y-3'>{children}</CardContent>
  </Card>
)

const StaffList = ({
  people,
}: {
  people: { id: string; name?: string; isDone: boolean; remarks?: string | null }[]
}) => (
  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
    {people.map((person) => (
      <div key={person.id} className='flex flex-col gap-1'>
        <span className='flex items-center gap-2 text-sm font-medium'>
          {person.isDone ? (
            <CheckCircle className='w-4 h-4 text-green-600' />
          ) : (
            <Circle className='w-4 h-4 text-muted-foreground' />
          )}
          {person.name || 'Unknown'}
        </span>
        {person.remarks && (
          <p className='text-xs text-muted-foreground whitespace-pre-wrap'>Remarks: {person.remarks}</p>
        )}
      </div>
    ))}
  </div>
)

const AccessMessage = ({ icon, title, message }: { icon: string; title: string; message: string }) => (
  <div className='flex items-center justify-center min-h-screen'>
    <div className='text-center'>
      <div className='text-red-600 text-6xl mb-4'>{icon}</div>
      <h2 className='text-2xl font-bold mb-2'>{title}</h2>
      <p className='text-gray-600 mb-4'>{message}</p>
      <Button onClick={() => (window.location.href = '/')}>
        <Home className='h-4 w-4' />
        Go Home
      </Button>
    </div>
  </div>
)

const ExemptionReviewAndApprovalPage = ({ type }: { type: 'review' | 'approval' }) => {
  const { token } = useParams()
  const { formatDate } = useDateFormat()
  const [data, setData] = useState<IExemptionReviewAndApprovalRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [remarks, setRemarks] = useState('')
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const fetchExemption = useCallback(async () => {
    if (!token) return
    try {
      const response = await PublicAxiosInstance.get(`/exemptions-register/review-and-approval/${token}`)
      setData(response.data.data)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load exemption'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void fetchExemption()
  }, [fetchExemption])

  const exemptionTitle = data ? data.exemption.exemptionDescription || 'Exemption' : null

  useEffect(() => {
    if (exemptionTitle) setPageTitle(exemptionTitle)
  }, [exemptionTitle])

  if (!token) return <Navigate to={'/'} />
  if (loading) return <FullPageSkeleton />
  if (error) return <AccessMessage icon='⚠️' title='Access Error' message={error} />
  if (!data) return <Navigate to={'/'} />

  const { exemption, assignment, accessInfo } = data
  const expiresAt = new Date(accessInfo.expiresAt)
  if (new Date() > expiresAt)
    return (
      <AccessMessage
        icon='⏰'
        title='Link Expired'
        message={`This exemption link has expired on ${expiresAt.toLocaleDateString()}`}
      />
    )

  const isReview = type === 'review'
  const actionLabel = isReview ? 'Review' : 'Approval'
  const doneLabel = isReview ? 'reviewed' : 'approved'
  // Reviewers act only while the exemption is under review, approvers only once it is pending approval
  const isActionableStage =
    exemption.status ===
    (isReview
      ? ExemptionsAndExemptionsRegisterStatus.UNDER_REVIEW
      : ExemptionsAndExemptionsRegisterStatus.PENDING_APPROVAL)
  const isRejecting = confirmAction === 'reject'

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await PublicAxiosInstance.post(`/exemptions-register/review-and-approval/${token}`, {
        isReject: isRejecting,
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      })
      setRemarks('')
      await fetchExemption()
    } catch (err) {
      throw new Error(getErrorMessage(err, `Failed to submit ${actionLabel.toLowerCase()}`))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='flex flex-col'>
      <div className='flex-shrink-0 p-4 border-b'>
        <div className='max-w-4xl mx-auto'>
          <p className='text-xs uppercase tracking-wide text-muted-foreground'>Exemption {actionLabel} Request</p>
          <h1 className='text-xl font-bold'>{exemption.exemptionDescription || 'Exemption'}</h1>
          <div className='flex items-center gap-2 mt-1'>
            <ColourBadge
              name={getExemptionStatusLabel(exemption.status)}
              colour={getGlobalStatusHexColor(exemption.status)}
            />
            <span className='text-sm text-muted-foreground'>Link expires {formatDate(accessInfo.expiresAt)}</span>
          </div>
        </div>
      </div>

      <div className='flex-1 p-4'>
        <div className='mx-auto max-h-[calc(100vh-320px)] max-w-4xl space-y-4 overflow-y-auto'>
          <Section icon={<FileText className='w-4 h-4' />} title='Basic Information'>
            <DetailRow label='Requestor' value={exemption.requestor?.name} />
            {exemption.policy?.name && <DetailRow label='Policy' value={exemption.policy.name} />}
            {exemption.organisationStandard?.standard?.name && (
              <DetailRow label='Organisation Standard' value={exemption.organisationStandard.standard.name} />
            )}
            {exemption.control?.name && <DetailRow label='Control' value={exemption.control.name} />}
            {exemption.risk?.name && <DetailRow label='Risk' value={exemption.risk.name} />}
            {exemption.status === ExemptionsAndExemptionsRegisterStatus.REJECTED && exemption.rejectionReason && (
              <DetailText label='Rejection Reason' value={exemption.rejectionReason} />
            )}
          </Section>

          <Section icon={<Calendar className='w-4 h-4' />} title='Timeline'>
            <DetailRow label='Start Date' value={exemption.startDate ? formatDate(exemption.startDate) : null} />
            <DetailRow
              label='Expiry/Review Date'
              value={exemption.expiryOrReviewDate ? formatDate(exemption.expiryOrReviewDate) : null}
            />
          </Section>

          <Section icon={<Shield className='w-4 h-4' />} title='Exemption Details'>
            <DetailText label='Exemption Description' value={exemption.exemptionDescription} />
            <DetailText label='Business Justification' value={exemption.businessJustification} />
            <DetailText label='Proposed Mitigation' value={exemption.proposedMitigation} />
          </Section>

          {exemption.risk?.residualRiskRating?.name && (
            <Section icon={<AlertTriangle className='w-4 h-4' />} title='Residual Risk Assessment'>
              <DetailRow label='Likelihood' value={exemption.risk.residualLikelihood?.name} />
              <DetailRow label='Impact' value={exemption.risk.residualImpact?.name} />
              <DetailRow label='Rating' value={exemption.risk.residualRiskRating.name} />
            </Section>
          )}

          {exemption.exemptionsReviewer?.length > 0 && (
            <Section icon={<Users className='w-4 h-4' />} title={`Reviewers (${exemption.exemptionsReviewer.length})`}>
              <StaffList
                people={exemption.exemptionsReviewer.map((r) => ({
                  id: r.id,
                  name: r.reviewer?.name,
                  isDone: r.isReviewed,
                  remarks: r.remarks,
                }))}
              />
            </Section>
          )}

          {exemption.exemptionsApprover?.length > 0 && (
            <Section
              icon={<CheckCircle className='w-4 h-4' />}
              title={`Approvers (${exemption.exemptionsApprover.length})`}
            >
              <StaffList
                people={exemption.exemptionsApprover.map((a) => ({
                  id: a.id,
                  name: a.approver?.name,
                  isDone: a.isApproved,
                  remarks: a.remarks,
                }))}
              />
            </Section>
          )}

          {exemption.exemptionsEvidence?.length > 0 && (
            <Section
              icon={<FileText className='w-4 h-4' />}
              title={`Evidence (${exemption.exemptionsEvidence.length})`}
            >
              {exemption.exemptionsEvidence.map((evidence) => (
                <div key={evidence.id} className='flex items-center justify-between p-2 bg-muted rounded-md'>
                  <span className='text-sm font-medium'>{evidence.label}</span>
                  <a
                    href={evidence.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:underline text-sm'
                  >
                    View Evidence
                  </a>
                </div>
              ))}
            </Section>
          )}
        </div>
      </div>

      <div className='flex-shrink-0 p-4'>
        <div className='max-w-4xl mx-auto flex flex-col gap-3 border-t border-border pt-4'>
          {assignment.isCompleted ? (
            <div className='space-y-1'>
              <p className='text-sm font-medium flex items-center gap-2'>
                <CheckCircle className='w-4 h-4 text-green-600' />
                You {doneLabel} this exemption
                {assignment.completedAt ? ` on ${formatDate(assignment.completedAt)}` : ''}
              </p>
              {assignment.remarks && (
                <p className='text-sm text-muted-foreground whitespace-pre-wrap'>Remarks: {assignment.remarks}</p>
              )}
            </div>
          ) : !isActionableStage ? (
            <p className='text-sm text-muted-foreground'>
              This exemption is {getExemptionStatusLabel(exemption.status).toLowerCase()} and is not awaiting your{' '}
              {actionLabel.toLowerCase()}.
            </p>
          ) : (
            <>
              <TextArea
                id='remarks'
                label='Remarks'
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder='Add remarks (required to reject)'
                className='h-[100px]'
              />
              <div className='flex gap-2 justify-end'>
                <Button variant='destructive' onClick={() => setConfirmAction('reject')}>
                  <X className='h-4 w-4' />
                  Reject
                </Button>
                <Button onClick={() => setConfirmAction('submit')}>
                  <Check className='h-4 w-4' />
                  Submit {actionLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isNested
        open={!!confirmAction}
        setOpen={(open) => {
          if (!open) setConfirmAction(null)
        }}
        onSubmit={handleSubmit}
        loading={submitting}
        submitDisabled={isRejecting && !remarks.trim()}
        submitButtonVariant={isRejecting ? 'destructive' : 'default'}
        cancelButtonVariant='outline'
        hideWarning={!isRejecting}
        title={isRejecting ? 'Reject Exemption' : `${isReview ? 'Review' : 'Approve'} Exemption`}
        description={
          isRejecting
            ? 'Are you sure you want to reject this exemption?'
            : `Are you sure you want to mark this exemption as ${doneLabel}?`
        }
        buttonLabel={isRejecting ? 'Confirm Reject' : `Confirm ${isReview ? 'Review' : 'Approve'}`}
        loadingButtonLabel={isRejecting ? 'Rejecting...' : 'Submitting...'}
        successMessageTitle={`Exemption ${isRejecting ? 'rejected' : doneLabel} successfully`}
        content={
          isRejecting && !remarks.trim() ? (
            <p className='text-sm text-destructive'>Please enter remarks explaining why it is being rejected.</p>
          ) : undefined
        }
      />
    </div>
  )
}

export default ExemptionReviewAndApprovalPage
